import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useQueryState } from 'nuqs';

import type {
  Citation,
  DisplayFilterGroup,
  ProductDisplayFields,
  SuggestedCTA,
  OnboardingStep,
} from '../services/types';
import type { ColorMode } from '../themes/theme';

export enum SystemMessageState {
  ASSISTANT = 'assistant',
  AI_FILTERS = 'ai-filters',
  NO_RESULTS = 'no-results',
  ERROR = 'error',
  HIDDEN = 'hidden',
  EXIT_INTENT = 'exit-intent',
}

export enum DisplayMode {
  ASSISTANT = 'assistant',
  SEARCH = 'search',
  NO_RESULTS = 'no-results',
  CATEGORY_ROUTE = 'category-route',
  ERROR = 'error',
}

export enum SuperBarViewState {
  DEFAULT = 'default',
  MINIMIZED = 'minimized',
  CLOSED = 'closed',
  EXPANDED = 'expanded',
  DISPLAYING_ANSWER = 'displaying_answer', // Same as default but keeps expanded width
}

interface SearchResult {
  products: ProductDisplayFields[];
  searchPagination: SearchPagination;
  id: string;
}

interface SearchPagination {
  currentPage: number;
  totalPages: number;
  count: number;
}

export interface DisplayMessage {
  userQuery?: string;
  systemAnswer?: string;
  suggestedCTA?: SuggestedCTA;
  nonDefinedCTA?: { label: string; url: string };
  hasCode?: boolean;
  id?: string;
  autoHideTimeout?: number;
  citations?: Citation[];
}

interface ErrorObject {
  errorMessage: string;
  error: Error;
}

type GlowVisualState = 'idle' | 'speaking';

interface ChatContextStateValues {
  displayMode: DisplayMode;
  displayMessage?: DisplayMessage;
  appliedFilterGroups: DisplayFilterGroup[];
  searchResults?: SearchResult;
  aiFiltersGroupsDisplayed: DisplayFilterGroup[];
  error?: ErrorObject;
  currentPage: number;
  searchId: string | null;
  urlSearchQuery?: string;
  enableSuperbarQuery?: string;
  isGalleryLoading: boolean;
  isLoadingAiFilters: boolean;
  showError: boolean;
  superBarViewState: SuperBarViewState;
  systemMessageState: SystemMessageState;
  productGalleryScrollPosition: number;
  remainingFilters: DisplayFilterGroup[];
  parentSearchId?: string;
  onboardingStep?: OnboardingStep;
  isSuperserveUi: boolean;
  mode: ColorMode;
  isPlayground: boolean;
  onPlaygroundCtaClick?: (url: string) => void;
  showFeedbackAddedNudge: boolean;
  pendingAskAgainQuery: string | null;
  isMenuOpen: boolean;
  isTextStreamActive: boolean;
  designatedCtas: SuggestedCTA[];
}

interface ChatContextStateFunctions {
  setAiFiltersGroupsDisplayed: (filters: DisplayFilterGroup[]) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  setDisplayMessage: (message: DisplayMessage | undefined) => void;
  updateDisplayMessageAnswer: (answer: string) => void;
  updateDisplayMessageMetadata: (metadata: {
    citations?: Citation[];
    suggestedCTA?: SuggestedCTA | null;
    nonDefinedCTA?: { label: string; url: string } | null;
  }) => void;
  setAppliedFilterGroups: (filters: DisplayFilterGroup[]) => void;
  setSearchResults: (searchResults: SearchResult) => void;
  setChatState: (args: Partial<ChatContextStateValues>) => void;
  setError: (e: ErrorObject | undefined) => void;
  clearAll: () => void;
  setCurrentPage: (page: number) => void;
  setSearchId: (searchId: string | null) => void;
  setIsGalleryLoading: (loading: boolean) => void;
  setIsFetchingAiFilters: (loading: boolean) => void;
  setShowError: (showError: boolean) => void;
  setSuperBarViewState: (state: SuperBarViewState) => void;
  setSystemMessageState: (state: SystemMessageState) => void;
  setProductGalleryScrollPosition: (position: number) => void;
  setUrlSearchQuery: (query: string) => void;
  setEnableSuperbarQuery: (enable: string) => void;
  setParentSearchId?: (searchId: string) => void;
  setOnboardingStep: (state: OnboardingStep | undefined) => void;
  setShowFeedbackAddedNudge: (v: boolean) => void;
  setPendingAskAgainQuery: (q: string | null) => void;
  setIsMenuOpen: (isOpen: boolean) => void;
  setIsTextStreamActive: (active: boolean) => void;
  setDesignatedCtas: (ctas: SuggestedCTA[]) => void;
}

type ChatContextState = ChatContextStateValues & ChatContextStateFunctions;

const chatContextDefaultValue: ChatContextState = {
  displayMode: DisplayMode.ASSISTANT,
  displayMessage: undefined,
  appliedFilterGroups: [],
  aiFiltersGroupsDisplayed: [],
  error: undefined,
  searchResults: undefined,
  currentPage: 1,
  searchId: null,
  isGalleryLoading: false,
  isLoadingAiFilters: false,
  showError: false,
  superBarViewState: SuperBarViewState.DEFAULT,
  systemMessageState: SystemMessageState.HIDDEN,
  productGalleryScrollPosition: 0,
  remainingFilters: [],
  enableSuperbarQuery: '',
  urlSearchQuery: undefined,
  onboardingStep: undefined,
  isSuperserveUi: false,
  isPlayground: false,
  onPlaygroundCtaClick: undefined,
  showFeedbackAddedNudge: false,
  pendingAskAgainQuery: null,
  mode: 'light',
  isMenuOpen: false,
  isTextStreamActive: false,
  designatedCtas: [],
  setAiFiltersGroupsDisplayed: () => undefined,
  setDisplayMode: () => undefined,
  setDisplayMessage: () => undefined,
  updateDisplayMessageAnswer: () => undefined,
  updateDisplayMessageMetadata: () => undefined,
  setSearchResults: () => undefined,
  setSearchId: () => undefined,
  clearAll: () => undefined,
  setAppliedFilterGroups: () => undefined,
  setChatState: () => undefined,
  setError: () => undefined,
  setCurrentPage: () => undefined,
  setIsGalleryLoading: () => undefined,
  setIsFetchingAiFilters: () => undefined,
  setShowError: () => undefined,
  setSuperBarViewState: () => undefined,
  setSystemMessageState: () => undefined,
  setProductGalleryScrollPosition: () => undefined,
  setUrlSearchQuery: () => undefined,
  setEnableSuperbarQuery: () => undefined,
  setOnboardingStep: () => undefined,
  setShowFeedbackAddedNudge: () => undefined,
  setPendingAskAgainQuery: () => undefined,
  setIsMenuOpen: () => undefined,
  setIsTextStreamActive: () => undefined,
  setDesignatedCtas: () => undefined,
};

const ChatContext = createContext<ChatContextState>(chatContextDefaultValue);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatContextProvider');
  }
  return context;
};

export const ChatContextProvider = ({
  children,
  isSuperserveUi,
  isPlayground,
  onPlaygroundCtaClick,
}: PropsWithChildren<{
  isSuperserveUi: boolean;
  isPlayground: boolean;
  onPlaygroundCtaClick?: (url: string) => void;
}>) => {
  const [displayMessage, setDisplayMessageState] = useState<
    DisplayMessage | undefined
  >(undefined);

  const setDisplayMessage = useCallback(
    (message: DisplayMessage | undefined) => {
      if (message === undefined) {
        setDisplayMessageState(undefined);
        return;
      }

      setDisplayMessageState({
        ...message,
        id: crypto.randomUUID(),
      });
    },
    []
  );
  const updateDisplayMessageAnswer = useCallback((answer: string) => {
    setDisplayMessageState((prev) => {
      if (!prev) return prev;
      return { ...prev, systemAnswer: answer };
    });
  }, []);
  const updateDisplayMessageMetadata = useCallback(
    (metadata: {
      citations?: Citation[];
      suggestedCTA?: SuggestedCTA | null;
      nonDefinedCTA?: { label: string; url: string } | null;
    }) => {
      setDisplayMessageState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(metadata.citations !== undefined && {
            citations: metadata.citations,
          }),
          ...(metadata.suggestedCTA !== undefined && {
            suggestedCTA: metadata.suggestedCTA ?? undefined,
          }),
          ...(metadata.nonDefinedCTA !== undefined && {
            nonDefinedCTA: metadata.nonDefinedCTA ?? undefined,
          }),
        };
      });
    },
    []
  );
  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    DisplayMode.ASSISTANT
  );
  const [searchResults, setSearchResults] = useState<SearchResult | undefined>(
    undefined
  );
  const [appliedFilterGroups, setAppliedFilterGroups] = useState<
    DisplayFilterGroup[]
  >([]);
  const [aiFiltersGroupsDisplayed, setAiFiltersGroupsDisplayed] = useState<
    DisplayFilterGroup[]
  >([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<ErrorObject | undefined>(undefined);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);
  const [isFetchingAiFilters, setIsFetchingAiFilters] = useState(false);
  const [showError, setShowError] = useState(false);
  const [superBarViewState, setSuperBarViewState] = useState(
    SuperBarViewState.DEFAULT
  );
  const [systemMessageState, setSystemMessageState] =
    useState<SystemMessageState>(SystemMessageState.HIDDEN);
  const [productGalleryScrollPosition, setProductGalleryScrollPosition] =
    useState(0);
  const [onboardingStep, setOnboardingStep] = useState<
    OnboardingStep | undefined
  >(undefined);
  const [showFeedbackAddedNudge, setShowFeedbackAddedNudge] = useState(false);
  const [pendingAskAgainQuery, setPendingAskAgainQuery] = useState<
    string | null
  >(null);
  const [mode, setMode] = useState<ColorMode>('light');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTextStreamActive, setIsTextStreamActive] = useState(false);
  const [designatedCtas, setDesignatedCtas] = useState<SuggestedCTA[]>([]);

  const handleSetOnboardingStep = useCallback(
    (state: OnboardingStep | undefined) => {
      setOnboardingStep(state);
    },
    []
  );
  const [searchId, setSearchId] = useQueryState('searchId');

  const [parentSearchId, setParentSearchId] = useState<string | undefined>(
    undefined
  );

  const [enableSuperbarQuery, setEnableSuperbarQuery] = useQueryState(
    'enableSuperbarQuery',
    {
      defaultValue: '',
    }
  );
  const [sbSearchQuery, setsbSearchQuery] = useQueryState('sbSearchQuery', {
    defaultValue: '',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: This function is intentionally not exhaustive
  const handleSetChatState = useCallback(
    (args: Partial<ChatContextStateValues>) => {
      if (args.displayMessage) {
        setDisplayMessage(args.displayMessage);
      }
      if (args.displayMode) {
        setDisplayMode(args.displayMode);
      }
      if (args.searchResults) {
        setSearchResults(args.searchResults);
        setSearchId(args.searchResults.id);
      }
      if (args.appliedFilterGroups) {
        setAppliedFilterGroups(args.appliedFilterGroups);
      }
      if (args.aiFiltersGroupsDisplayed) {
        setAiFiltersGroupsDisplayed(args.aiFiltersGroupsDisplayed);
      }
      if (args.currentPage) {
        setCurrentPage(args.currentPage);
      }
      if (args.parentSearchId) {
        setParentSearchId(args.parentSearchId);
      }
      if (args.onboardingStep !== undefined) {
        handleSetOnboardingStep(args.onboardingStep);
      }
      if (args.mode !== undefined) {
        setMode(args.mode);
      }
      if (args.isMenuOpen !== undefined) {
        setIsMenuOpen(args.isMenuOpen);
      }
      if (args.designatedCtas !== undefined) {
        setDesignatedCtas(args.designatedCtas);
      }
    },
    [
      setDisplayMessage,
      setDisplayMode,
      setSearchResults,
      setSearchId,
      setAppliedFilterGroups,
      setAiFiltersGroupsDisplayed,
      setCurrentPage,
      setParentSearchId,
      handleSetOnboardingStep,
      setMode,
      setIsMenuOpen,
      setDesignatedCtas,
    ]
  );

  const remainingFilters = useMemo(
    () =>
      aiFiltersGroupsDisplayed.filter(
        (filterGroup) =>
          !appliedFilterGroups.find(
            (selected) =>
              selected.label === filterGroup.label &&
              selected.value === filterGroup.value
          )
      ),
    [aiFiltersGroupsDisplayed, appliedFilterGroups]
  );

  const isLoadingAiFilters = useMemo(() => {
    return (
      systemMessageState === SystemMessageState.AI_FILTERS &&
      (isFetchingAiFilters ||
        isGalleryLoading ||
        !displayMessage?.systemAnswer ||
        !remainingFilters.length)
    );
  }, [
    systemMessageState,
    isFetchingAiFilters,
    isGalleryLoading,
    displayMessage?.systemAnswer,
    remainingFilters.length,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: This function is intentionally not exhaustive
  const clearAll = useCallback(() => {
    setDisplayMessage(undefined);
    setDisplayMode(DisplayMode.ASSISTANT);
    setSearchResults(undefined);
    setSearchId(null);
    setAppliedFilterGroups([]);
    setAiFiltersGroupsDisplayed([]);
    setCurrentPage(1);
    setParentSearchId(undefined);
    setOnboardingStep(undefined);
    setIsMenuOpen(false);
    setDesignatedCtas([]);
  }, [
    setDisplayMessage,
    setDisplayMode,
    setSearchResults,
    setSearchId,
    setAppliedFilterGroups,
    setAiFiltersGroupsDisplayed,
    setCurrentPage,
    setParentSearchId,
    setOnboardingStep,
    setIsMenuOpen,
    setDesignatedCtas,
  ]);

  const value = {
    displayMode,
    displayMessage,
    searchResults,
    appliedFilterGroups,
    aiFiltersGroupsDisplayed,
    error,
    currentPage,
    searchId,
    urlSearchQuery: sbSearchQuery,
    isGalleryLoading,
    isLoadingAiFilters,
    showError,
    superBarViewState,
    systemMessageState,
    productGalleryScrollPosition,
    remainingFilters,
    isFetchingAiFilters,
    enableSuperbarQuery,
    parentSearchId,
    onboardingStep,
    isSuperserveUi,
    isPlayground,
    onPlaygroundCtaClick,
    showFeedbackAddedNudge,
    pendingAskAgainQuery,
    mode,
    isMenuOpen,
    isTextStreamActive,
    designatedCtas,
    setDisplayMode,
    setDisplayMessage,
    updateDisplayMessageAnswer,
    updateDisplayMessageMetadata,
    setSearchResults,
    setAppliedFilterGroups,
    setAiFiltersGroupsDisplayed,
    clearAll,
    setChatState: handleSetChatState,
    setError,
    setCurrentPage,
    setSearchId,
    setIsGalleryLoading,
    setIsFetchingAiFilters,
    setShowError,
    setSuperBarViewState,
    setSystemMessageState,
    setProductGalleryScrollPosition,
    setUrlSearchQuery: setsbSearchQuery,
    setEnableSuperbarQuery,
    setParentSearchId,
    setOnboardingStep: handleSetOnboardingStep,
    setShowFeedbackAddedNudge,
    setPendingAskAgainQuery,
    setIsMenuOpen,
    setIsTextStreamActive,
    setDesignatedCtas,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
